import styled from 'styled-components';

import { Slide } from '@components/animators/slide';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { FormProps, createForm } from '@components/form';
import { UnsavedChangesPrompter } from '@components/generic-modals';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { SelectOption } from '@components/select';
import { Sidebar, forceHideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { Color, theme } from '@styles/theme';

import { AttributeFieldType, TravellerAttributeTemplateData } from 'api/attributes/requests';
import { visibilityMap } from 'api/journeys/requests';

import { categoryColors, fieldTypeConfig, getFieldTypesByCategory } from '../common';

// =============================================================================
// Types & form
// =============================================================================

export type FieldFormData = {
  title: string;
  attributeType: AttributeFieldType;
  input?: 'required' | 'optional' | 'disabled';
  options?: string;
  multipleFiles?: boolean;
  isDecimal?: boolean;
};

export const Form = createForm<FieldFormData>();

export const isSimpleFieldType = (type: AttributeFieldType) => {
  return (
    type === 'text' ||
    type === 'paragraph' ||
    type === 'date' ||
    type === 'email' ||
    type === 'phone_number' ||
    type === 'user'
  );
};

export const isNumberFieldType = (type: AttributeFieldType) => {
  return type === 'number' || type === 'decimal';
};

export const isFileFieldType = (type: AttributeFieldType) => {
  return type === 'file' || type === 'file_multi';
};

export const isSelectFieldType = (
  type: AttributeFieldType
): type is 'single_select' | 'multi_select' => {
  return type === 'single_select' || type === 'multi_select';
};

export const applyInputStatus = (
  data: FieldFormData,
  isTravellerAttribute: boolean
): FieldFormData => {
  const inputStatus =
    isTravellerAttribute && data.input && visibilityMap[data.input]
      ? visibilityMap[data.input]
      : {};
  const result: FieldFormData = { ...data, ...inputStatus };
  delete result.input;
  return result;
};

// =============================================================================
// Form wrapper with unsaved-changes prompt
// =============================================================================

const fieldFormPromptLocale = {
  en: {
    saveChangesTitle: 'Save field?',
    hasUnsaved:
      'The edits you have made to this field have not been saved, would you like to save or discard your changes?',
    saveChanges: 'Save',
  },
};

type FieldFormWithUnsavedChangesPromptProps = FormProps & {
  overwriteIsDirty?: boolean;
};

export const FieldFormWithUnsavedChangesPrompt = ({
  children,
  overwriteIsDirty,
  ...rest
}: FieldFormWithUnsavedChangesPromptProps) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Form {...rest}>
      <UnsavedChangesPrompter
        onSubmit={rest.onSubmit}
        locale={fieldFormPromptLocale}
        discardText={t('discardChanges')}
        overwriteIsDirty={overwriteIsDirty}
      />
      {children}
    </Form>
  );
};

// =============================================================================
// Section layout
// =============================================================================

type FieldFormSectionProps = {
  label: string;
  children: React.ReactNode;
};

export const FieldFormSection = ({ label, children }: FieldFormSectionProps) => {
  return (
    <SectionContainer>
      <Text text={label} font="semibold14" />
      <div style={{ minWidth: 0 }}>{children}</div>
    </SectionContainer>
  );
};

const SectionContainer = styled.div({
  display: 'grid',
  gridTemplateColumns: '2fr 4fr',
});

// =============================================================================
// Type card (shown for each AttributeFieldType)
// =============================================================================

type TypeCardProps = {
  attributeType: AttributeFieldType;
  onClick?: () => void;
};

export const TypeCard = ({ attributeType, onClick }: TypeCardProps) => {
  const { t } = useScopedTranslate(locale);
  const config = fieldTypeConfig[attributeType];
  const Icon = config.icon;

  return (
    <TypeCardStyled
      space={3}
      variant="transparent"
      onClick={onClick}
      data-category={config.category}
      data-clickable={Boolean(onClick)}
    >
      <Stack space={4} orientation="horizontal" align="center">
        <Icon color={categoryColors[config.category]} />
        <Stack space={1}>
          <Text text={t(config.labelKey as keyof typeof locale.en)} font="semibold14" />
          <Text
            text={t(config.descriptionKey as keyof typeof locale.en)}
            font="regular12"
            color="textSecondary"
          />
        </Stack>
      </Stack>
    </TypeCardStyled>
  );
};

const TypeCardStyled = styled(Card)({
  '&[data-category="inputs"]': {
    background: theme.color.orangeTrans5,
  },
  '&[data-category="selections"]': {
    background: theme.color.blueTrans5,
  },
  '&[data-clickable="true"]': {
    cursor: 'pointer',
    '&[data-category="inputs"]:hover': {
      background: theme.color.orangeTrans10,
    },
    '&[data-category="selections"]:hover': {
      background: theme.color.blueTrans10,
    },
  },
});

// =============================================================================
// Field type selector (initial step when creating a field)
// =============================================================================

type FieldTypeSelectorProps = {
  onSelectType: (type: AttributeFieldType) => void;
};

export const FieldTypeSelector = ({ onSelectType }: FieldTypeSelectorProps) => {
  const { t } = useScopedTranslate(locale);

  const inputTypes = getFieldTypesByCategory('inputs');
  const selectionTypes = getFieldTypesByCategory('selections');

  return (
    <Slide key="field-type-selector" offset={-30}>
      <Stack space={6}>
        <FieldFormSection label={t('inputs')}>
          <Stack space={2}>
            {inputTypes.map((type) => {
              return (
                <TypeCard
                  key={type}
                  attributeType={type}
                  onClick={() => {
                    return onSelectType(type);
                  }}
                />
              );
            })}
          </Stack>
        </FieldFormSection>

        <Sidebar.Separator />

        <FieldFormSection label={t('selections')}>
          <Stack space={2}>
            {selectionTypes.map((type) => {
              return (
                <TypeCard
                  key={type}
                  attributeType={type}
                  onClick={() => {
                    return onSelectType(type);
                  }}
                />
              );
            })}
          </Stack>
        </FieldFormSection>
      </Stack>
    </Slide>
  );
};

// =============================================================================
// Input status options (for traveller fields)
// =============================================================================

const InputOptionLabel = ({ label, color }: { label: string; color: Color }) => {
  return (
    <Stack orientation="horizontal" align="center" space={1}>
      <IconStatusIndicator color={color} />
      <Text text={label} font="regular14" />
    </Stack>
  );
};

const useInputStatusOptions = () => {
  const { t } = useScopedTranslate(locale);

  const inputOptions: SelectOption[] = [
    {
      value: 'required',
      label: <InputOptionLabel label={t('required')} color="elementGreen100" />,
    },
    {
      value: 'optional',
      label: <InputOptionLabel label={t('optional')} color="elementOrange100" />,
    },
    {
      value: 'disabled',
      label: <InputOptionLabel label={t('disabled')} color="elementPurple50" />,
    },
  ];

  return inputOptions;
};

export const getInputType = (attribute?: TravellerAttributeTemplateData) => {
  if (!attribute) {
    return 'optional';
  }
  if (attribute.isRequired) {
    return 'required';
  }
  if (attribute.isActive) {
    return 'optional';
  }
  return 'disabled';
};

// =============================================================================
// Form footer (cancel + submit)
// =============================================================================

type FormFooterProps = {
  isEditing: boolean;
  isDirty: boolean;
  isDisabled?: boolean;
};

export const FormFooter = ({ isEditing, isDirty, isDisabled }: FormFooterProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Sidebar.Footer>
      <Form.Actions>
        <Button text={t('core.cancel')} variant="secondary" onClick={forceHideSidebar} />
        <Form.Submit
          text={isEditing ? t('core.save') : t('createField')}
          variant="primary-blue"
          isDisabled={!isDirty || isDisabled}
        />
      </Form.Actions>
    </Sidebar.Footer>
  );
};

// =============================================================================
// Details section (shared by every form variant)
// =============================================================================

type DetailsSectionProps = {
  attributeType: AttributeFieldType;
  attribute?: TravellerAttributeTemplateData;
  isTravellerAttribute: boolean;
  children?: React.ReactNode;
};

export const DetailsSection = ({
  attributeType,
  attribute,
  isTravellerAttribute,
  children,
}: DetailsSectionProps) => {
  const { t } = useScopedTranslate(locale);
  const inputOptions = useInputStatusOptions();
  const inputType = getInputType(attribute);

  return (
    <FieldFormSection label={t('details')}>
      <Stack space={4}>
        <TypeCard attributeType={attributeType} />
        {children}
        <Form.TextField
          id="title"
          label={t('fieldLabel')}
          validators={['required']}
          defaultValue={attribute?.title}
        />
        {isTravellerAttribute && (
          <Form.Select
            id="input"
            label={t('input')}
            validators={['required']}
            defaultValue={inputType}
            options={inputOptions}
          />
        )}
      </Stack>
    </FieldFormSection>
  );
};

// =============================================================================
// Locale
// =============================================================================

export const locale = {
  en: {
    // Section labels
    details: 'Details',
    formatting: 'Formatting',
    options: 'Options',
    inputs: 'Inputs',
    selections: 'Selections',
    listType: 'List type',
    selectList: 'Select list',

    // Field type labels (from fieldTypeConfig)
    singleLineText: 'Single line text',
    singleLineTextDescription: 'Small general input field',
    longText: 'Long text',
    longTextDescription: 'Large input field',
    number: 'Number',
    numberDescription: 'Small input field for numbers',
    emailAddress: 'Email address',
    emailAddressDescription: 'For adding additional emails',
    phoneNumber: 'Phone number',
    phoneNumberDescription: 'For adding phone numbers',
    date: 'Date',
    dateDescription: 'For selecting dates',
    fileUpload: 'File upload',
    fileUploadDescription: 'For file attachments',
    singleChoice: 'Single choice',
    singleChoiceDescription: 'For selecting a single option from a list',
    multipleChoice: 'Multiple choice',
    multipleChoiceDescription: 'For selecting multiple options from a list',
    selectUser: 'User',
    selectUserDescription: 'For selecting users in the system',
    boolean: 'On / off',
    booleanDescription: 'For selecting a boolean value',

    // Form labels
    fieldLabel: 'Field label',
    input: 'Input',
    required: 'Required',
    optional: 'Optional',
    disabled: 'Disabled',
    allowDecimalNumbers: 'Allow decimal numbers',
    allowMultipleFiles: 'Allow multiple files',
    example: 'Example',

    // Select field flow
    selectFromGlobalLists: 'Select from existing global lists',
    selectFromMarketplace: 'Select from marketplace integrations',
    selectFromMarketplaceComingSoon: 'Select from marketplace integrations (Coming soon)',
    searchForList: 'Search for list...',
    searchForIntegration: 'Search for integration...',
    selectIntegration: 'Select integration',
    listInsideExternalForm: 'List inside External form',
    listNotFound: 'List not found',
    noLists: 'No lists',
    noListsDescription: "You haven't created any lists yet",
    noIntegrationListsDescription: "This integration doesn't have any lists",
    noConnectedApps: 'No connected apps',
    noResultsFound: 'No results found',
    noResultsFoundDescription: 'Try adjusting your search',
    select: 'Select',
    replaceList: 'Replace list',

    // Actions
    createField: 'Create field',
    createJourneyField: 'Create Journey field',
    editJourneyField: 'Edit Journey field',
    createTravellerField: 'Create Traveller field',
    editTravellerField: 'Edit Traveller field',
    updateField: 'Update field',
    updateFieldDescription:
      'This update will affect {num_travellers} {travellerNoun}.\nAre you sure you want to update this field?',
    traveller: 'traveller',
    travellers: 'travellers',
    canNotEditSystemField: 'System fields can not be edited or removed',
    discardChanges: 'Discard changes',
  },
};
